// AdminCastingPage — casting: state a need (catalog dropdowns) → ranked compatible casts → SELECT or
// GENERATE. Matches the admin warm theme. On SELECT, calls onSelect(stockId) (the growth flow wires this
// to updateAccount(characterRegistryId)). Standalone here; embed as a panel or route.
import { useState, useEffect } from "react";
import { castingApi, type NeededPersona, type CastingCandidate } from "../../api/casting.ts";

const INK = "#0a0a0a", MUTE = "#6b6b6b", LINE = "#e9e6df", ACCENT = "#ff5b1f";
const card: React.CSSProperties = { background: "#fff", border: `1px solid ${LINE}`, borderRadius: 12, padding: 16 };
const btn = (p = false): React.CSSProperties => ({ padding: "8px 14px", borderRadius: 8, fontWeight: 600, fontSize: 13, cursor: "pointer", border: p ? "none" : `1px solid ${LINE}`, background: p ? ACCENT : "#fff", color: p ? "#fff" : INK });

// the catalog value lists (mirror tubeforge-shared casting-contract — kept in sync via the shared package;
// duplicated here only for the dropdown options. If you import from @vms/tubeforge-shared in the frontend, use that.)
const GENDER = ["F", "M"];
const AGE = ["18-25", "25-35", "35-45", "45-55", "55+"];
const ETH = ["White", "Hispanic", "Black", "East Asian", "South Asian", "Southeast Asian", "Middle Eastern", "Mixed"];
const STYLE = ["casual", "polished", "professional"];
const BUILD = ["slim", "average", "athletic", "curvy", "heavyset", "plus_size"];
const GLASSES = ["none", "glasses", "sunglasses"];

export function AdminCastingPage({ onSelect }: { onSelect?: (stockId: string) => void }) {
  const [need, setNeed] = useState<NeededPersona>({});
  const [roster, setRoster] = useState<CastingCandidate[] | null>(null);
  const [ignored, setIgnored] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [genState, setGenState] = useState<string | null>(null);
  const [voices, setVoices] = useState<Array<{ id: string; name: string }>>([]);
  const [voiceId, setVoiceId] = useState<string>("");

  useEffect(() => { castingApi.listVoices().then((r) => { setVoices(r.voices); if (r.voices[0]) setVoiceId(r.voices[0].id); }).catch(() => {}); }, []);

  const setPA = (k: string, v: string) =>
    setNeed((n) => ({ ...n, physicalAttributes: { ...(n.physicalAttributes ?? {}), ...(v ? { [k]: v } : {}) } }));

  const search = async () => {
    setBusy(true); setErr(null);
    try { const r = await castingApi.roster(need); setRoster(r.roster); setIgnored(r.ignored ?? []); }
    catch (e) { setErr(String(e)); } finally { setBusy(false); }
  };

  const generate = async () => {
    if (!need.gender || !need.ageRange || !need.ethnicity || !need.style) {
      setErr("To generate, pick at least gender, age, ethnicity, and style."); return;
    }
    if (!voiceId) { setErr("Pick a voice to generate a character."); return; }
    setBusy(true); setErr(null); setGenState("Generating…");
    try {
      const { stockId } = await castingApi.generate({ ...need, voiceCatalogId: voiceId });
      // poll until ready, then it's selectable
      const poll = setInterval(async () => {
        try {
          const { character } = await castingApi.getCharacter(stockId);
          setGenState(`Generating… (${character.state})`);
          if (character.state === "ready") { clearInterval(poll); setGenState(null); setBusy(false); search(); }
          else if (character.state === "failed") { clearInterval(poll); setGenState(null); setBusy(false); setErr("Generation failed — try again."); }
        } catch { /* keep polling */ }
      }, 8000);
    } catch (e) { setErr(String(e)); setBusy(false); setGenState(null); }
  };

  const sel = (v: string | undefined) => v ?? "";
  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: "0 auto", color: INK }}>
      <h1 style={{ fontSize: 22, fontWeight: 700 }}>Casting</h1>
      <p style={{ color: MUTE, fontSize: 13, marginTop: 2 }}>Describe the character you need. We'll show the best matches — or generate a new one.</p>

      {/* the need form — catalog dropdowns (never free text) */}
      <div style={{ ...card, marginTop: 16, display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(150px,1fr))", gap: 10 }}>
        <Field label="Gender"><Select v={sel(need.gender)} opts={GENDER} onChange={(v) => setNeed((n) => ({ ...n, gender: (v || undefined) as NeededPersona["gender"] }))} /></Field>
        <Field label="Age"><Select v={sel(need.ageRange)} opts={AGE} onChange={(v) => setNeed((n) => ({ ...n, ageRange: v || undefined }))} /></Field>
        <Field label="Ethnicity"><Select v={sel(need.ethnicity)} opts={ETH} onChange={(v) => setNeed((n) => ({ ...n, ethnicity: v || undefined }))} /></Field>
        <Field label="Style"><Select v={sel(need.style)} opts={STYLE} onChange={(v) => setNeed((n) => ({ ...n, style: v || undefined }))} /></Field>
        <Field label="Build"><Select v={sel(need.physicalAttributes?.build)} opts={BUILD} onChange={(v) => setPA("build", v)} /></Field>
        <Field label="Glasses"><Select v={sel(need.physicalAttributes?.glasses)} opts={GLASSES} onChange={(v) => setPA("glasses", v)} /></Field>
        <Field label="Voice (for generate)"><Select v={voiceId} opts={voices.map((v) => v.id)} onChange={setVoiceId} /></Field>
        <Field label="Must speak (talking-head)"><input type="checkbox" checked={need.capability === "talking_head"} onChange={(e) => setNeed((n) => ({ ...n, capability: e.target.checked ? "talking_head" : undefined }))} /></Field>
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button disabled={busy} onClick={search} style={btn(true)}>{busy && !genState ? "Searching…" : "Find matches"}</button>
        <button disabled={busy} onClick={generate} style={btn()}>{genState ?? "Generate a new one"}</button>
      </div>
      {err && <div style={{ color: "#b91c1c", fontSize: 13, marginTop: 8 }}>{err}</div>}
      {ignored.length > 0 && <div style={{ color: "#a60", fontSize: 12, marginTop: 6 }}>Ignored (not in catalog): {ignored.join("; ")}</div>}

      {/* the ranked roster */}
      {roster && (
        <div style={{ marginTop: 20 }}>
          <div style={{ fontWeight: 700, marginBottom: 8 }}>{roster.length} matches (best fit first)</div>
          {roster.length === 0 ? <div style={{ ...card, color: MUTE }}>No matches. Generate a new character with these attributes.</div> : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 12 }}>
              {roster.map((c) => (
                <div key={c.stockId} style={{ ...card, padding: 0, overflow: "hidden" }}>
                  {c.previewUrl
                    ? <img src={c.previewUrl} alt={c.displayName ?? ""} style={{ width: "100%", aspectRatio: "3/4", objectFit: "cover", display: "block", background: "#f3efe5" }} />
                    : <div style={{ width: "100%", aspectRatio: "3/4", background: "#f3efe5", display: "flex", alignItems: "center", justifyContent: "center", color: MUTE, fontSize: 12 }}>no preview</div>}
                  <div style={{ padding: 10 }}>
                    <div style={{ fontWeight: 600, fontSize: 13 }}>{c.displayName ?? "Unnamed"}</div>
                    <div style={{ fontSize: 11, color: MUTE, marginTop: 2 }}>
                      {[c.gender, c.ageRange, c.ethnicity].filter(Boolean).join(" · ")}
                    </div>
                    <div style={{ fontSize: 11, color: ACCENT, marginTop: 2 }}>
                      fit {c.score}{c.capability.talkingHead ? " · speaks" : ""}
                    </div>
                    <button onClick={() => onSelect?.(c.stockId)} style={{ ...btn(true), width: "100%", marginTop: 8 }}>Select</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <label style={{ fontSize: 12, color: MUTE }}>{label}<div style={{ marginTop: 4 }}>{children}</div></label>;
}
function Select({ v, opts, onChange }: { v: string; opts: string[]; onChange: (v: string) => void }) {
  return (
    <select value={v} onChange={(e) => onChange(e.target.value)} style={{ width: "100%", padding: 6, borderRadius: 8, border: `1px solid ${LINE}` }}>
      <option value="">any</option>
      {opts.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}
